import React, { useState, useEffect, useRef } from 'react';
import { useColorMode } from '@chakra-ui/react';
import { css } from '@emotion/react';
import { colors, fontStyles, spacing } from '../../../../styles';

import claudeIcon from '../../../../assets/Icons/claude.svg';
import openaiIcon from '../../../../assets/Icons/openai.svg';
import geminiIcon from '../../../../assets/Icons/gemini.svg';

interface DailyPicksAnimationProps {
  isActive: boolean;
}

const MODELS = [
  { id: 'openai', name: 'GPT', icon: openaiIcon, return: 32.0 },
  { id: 'gemini', name: 'Gemini', icon: geminiIcon, return: 17.0 },
  { id: 'claude', name: 'Claude', icon: claudeIcon, return: 15.0 },
];

// Canvas chart data — percentage return values over 40 time steps
// GPT climbs steeply to +32%, Gemini/Claude track together ending at +17%/+15%
const MODEL_DATA: Record<string, number[]> = {
  openai: [
    0, -0.5, 0.3, -1.0, -1.8, -0.8, 0.8, 2.2, 1.5, 3.5, 5.5, 4.8, 7.2, 9.8, 9.0, 12.0, 14.0, 16.5, 15.0, 18.5, 20.5,
    19.8, 22.5, 24.0, 23.2, 25.8, 27.2, 26.5, 28.5, 29.8, 29.0, 30.5, 31.0, 30.5, 31.5, 31.8, 31.2, 31.8, 32.2, 32.0,
  ],
  gemini: [
    0, 0.3, -0.2, 0.8, -0.6, 0.8, 2.2, 1.8, 3.2, 2.8, 4.5, 4.0, 6.0, 6.5, 6.0, 8.0, 8.5, 8.0, 10.0, 10.5, 10.0, 12.0,
    12.5, 12.0, 13.5, 14.0, 13.5, 15.0, 15.5, 15.0, 16.0, 16.5, 16.0, 16.8, 17.0, 16.6, 17.2, 16.8, 17.5, 17.0,
  ],
  claude: [
    0, 0.2, 0.5, 0.1, -0.4, 0.5, 1.8, 1.2, 2.8, 2.2, 3.8, 3.2, 5.2, 5.8, 5.2, 7.2, 7.8, 7.2, 9.2, 9.8, 9.2, 11.0, 11.5,
    11.0, 12.5, 13.0, 12.5, 13.5, 14.0, 13.5, 14.5, 14.8, 14.3, 15.0, 14.7, 14.5, 15.2, 14.8, 15.5, 15.0,
  ],
};

// Y-axis range (adds headroom above 32%)
const Y_MIN = -2.5;
const Y_MAX = 35;

// Y-axis tick labels drawn on canvas
const Y_AXIS_TICKS = [
  { label: '0%', pct: 0 },
  { label: '10%', pct: 10 },
  { label: '20%', pct: 20 },
  { label: '30%', pct: 30 },
];

// Chart line colors (light mode)
const CHART_COLORS: Record<string, string> = {
  claude: '#F47B20',
  openai: '#222222',
  gemini: '#4285F4',
};

// Chart line colors (dark mode)
const CHART_COLORS_DARK: Record<string, string> = {
  claude: '#F47B20',
  openai: '#FFFFFF',
  gemini: '#4285F4',
};

// Badge pill labels
const BADGE_LABELS: Record<string, string> = {
  openai: '+32%',
  claude: '+15%',
  gemini: '+17%',
};

// Last data point percentage for each model (used to position badges)
// Positions are nudged apart to prevent pill overlap at larger badge size
const BADGE_TOP_PCT: Record<string, number> = {
  openai: 12,
  gemini: 42,
  claude: 52,
};

// Icon lookup for badge pills
const MODEL_ICONS: Record<string, string> = {
  claude: claudeIcon,
  openai: openaiIcon,
  gemini: geminiIcon,
};

export const DailyPicksAnimation: React.FC<DailyPicksAnimationProps> = ({ isActive }) => {
  const { colorMode } = useColorMode();
  const [visibleCards, setVisibleCards] = useState<number[]>([]);
  const [showChart, setShowChart] = useState(false);
  const [chartProgress, setChartProgress] = useState(0);
  const timeoutIdsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const rafRef = useRef<number | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    timeoutIdsRef.current.forEach((id) => clearTimeout(id));
    timeoutIdsRef.current = [];
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);

    if (isActive) {
      setVisibleCards([]);
      setShowChart(false);
      setChartProgress(0);

      // Stagger cards in
      MODELS.forEach((_, index) => {
        const id = setTimeout(
          () => {
            setVisibleCards((prev) => [...prev, index]);
          },
          150 + index * 300,
        );
        timeoutIdsRef.current.push(id);
      });

      // Show chart then animate lines
      const chartId = setTimeout(() => {
        setShowChart(true);
        const start = performance.now();
        const duration = 900;
        const animate = (now: number) => {
          const elapsed = now - start;
          const progress = Math.min(elapsed / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setChartProgress(eased);
          if (progress < 1) {
            rafRef.current = requestAnimationFrame(animate);
          }
        };
        rafRef.current = requestAnimationFrame(animate);
      }, 1100);
      timeoutIdsRef.current.push(chartId);
    }

    return () => {
      timeoutIdsRef.current.forEach((id) => clearTimeout(id));
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [isActive]);

  // Draw chart on canvas whenever chartProgress changes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !showChart) return;

    const dpr = window.devicePixelRatio || 1;
    const W = canvas.offsetWidth;
    const H = canvas.offsetHeight;
    if (W === 0 || H === 0) return;

    // Only resize the backing buffer when dimensions change — resizing is expensive
    // (forces a reflow) and also clears the canvas implicitly.
    if (canvas.width !== W * dpr || canvas.height !== H * dpr) {
      canvas.width = W * dpr;
      canvas.height = H * dpr;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    // Use setTransform (not scale) so the DPR transform is always correct
    // regardless of whether we resized above.
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // Layout: leave left margin for y-axis labels, right margin for badges
    const padLeft = 28;
    const padRight = 6;
    const padTop = 4;
    const padBottom = 16; // extra room for x-axis date labels
    const chartW = W - padLeft - padRight;
    const chartH = H - padTop - padBottom;

    const toX = (i: number) => padLeft + (i / (MODEL_DATA.openai.length - 1)) * chartW;
    const toY = (pct: number) => padTop + chartH - ((pct - Y_MIN) / (Y_MAX - Y_MIN)) * chartH;

    const isDark = colorMode === 'dark';

    ctx.clearRect(0, 0, W, H);

    // --- Grid lines and y-axis labels ---
    ctx.save();
    Y_AXIS_TICKS.forEach(({ label, pct }) => {
      const y = toY(pct);

      // Grid line
      ctx.beginPath();
      ctx.setLineDash([3, 4]);
      ctx.strokeStyle = isDark ? '#444444' : '#DDDDDD';
      ctx.lineWidth = 0.5;
      ctx.moveTo(padLeft, y);
      ctx.lineTo(W - padRight, y);
      ctx.stroke();

      // Y-axis label
      ctx.setLineDash([]);
      ctx.fillStyle = '#AAAAAA';
      ctx.font = `10px sans-serif`;
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, padLeft - 4, y);
    });
    ctx.restore();

    // --- X-axis date labels ---
    ctx.fillStyle = '#AAAAAA';
    ctx.font = `10px sans-serif`;
    ctx.textBaseline = 'bottom';
    ctx.setLineDash([]);
    ctx.textAlign = 'left';
    ctx.fillText('Dec 2', padLeft, H - 1);
    ctx.textAlign = 'right';
    ctx.fillText('Mar 2', W - padRight, H - 1);

    // --- Clip to animated portion (grows left-to-right as chartProgress: 0→1) ---
    ctx.save();
    const clipX = padLeft + chartW * chartProgress;
    ctx.beginPath();
    ctx.rect(0, 0, clipX, H);
    ctx.clip();

    // --- Draw each model's line using Catmull-Rom splines ---
    const modelOrder = ['openai', 'gemini', 'claude']; // draw order (back to front)
    modelOrder.forEach((modelId) => {
      const data = MODEL_DATA[modelId];
      const color = isDark ? CHART_COLORS_DARK[modelId] : CHART_COLORS[modelId];
      const points = data.map((pct, i) => ({ x: toX(i), y: toY(pct) }));

      ctx.beginPath();
      ctx.strokeStyle = color;
      ctx.lineWidth = 1.8;
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';

      ctx.moveTo(points[0].x, points[0].y);
      for (let i = 0; i < points.length - 1; i++) {
        const p0 = points[Math.max(0, i - 1)];
        const p1 = points[i];
        const p2 = points[i + 1];
        const p3 = points[Math.min(points.length - 1, i + 2)];
        const cp1x = p1.x + (p2.x - p0.x) / 6;
        const cp1y = p1.y + (p2.y - p0.y) / 6;
        const cp2x = p2.x - (p3.x - p1.x) / 6;
        const cp2y = p2.y - (p3.y - p1.y) / 6;
        ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, p2.x, p2.y);
      }
      ctx.stroke();
    });

    ctx.restore();
  }, [chartProgress, showChart, colorMode]);

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        padding: 4px ${spacing.deci} ${spacing.deci};
        gap: ${spacing.centi};
        height: 100%;
        overflow: hidden;
      `}
    >
      {/* Model performance cards */}
      <div
        css={css`
          display: flex;
          gap: ${spacing.centi};
        `}
      >
        {MODELS.map((model, index) => {
          const isVisible = visibleCards.includes(index);
          return (
            <div
              key={model.id}
              css={css`
                flex: 1;
                display: flex;
                flex-direction: row;
                align-items: center;
                gap: 6px;
                padding: ${spacing.centi};
                background: ${colors.sugar};
                border-radius: 10px;
                opacity: ${isVisible ? 1 : 0};
                transform: translateY(${isVisible ? 0 : 16}px);
                transition:
                  opacity 0.35s ease-out,
                  transform 0.35s ease-out;
                html[data-theme='dark'] & {
                  background: ${colors.obsidian};
                }
              `}
            >
              <img
                src={model.icon}
                alt={model.name}
                css={css`
                  width: 13px;
                  height: 13px;
                  flex-shrink: 0;
                  html[data-theme='dark'] & {
                    filter: ${model.id === 'openai' ? 'invert(1)' : 'none'};
                  }
                `}
              />
              <span
                css={css`
                  ${fontStyles.flea};
                  color: ${colors.tungsten};
                  html[data-theme='dark'] & {
                    color: ${colors.silver};
                  }
                `}
              >
                {model.name}
              </span>
              <span
                css={css`
                  ${fontStyles.flea};
                  font-weight: 700;
                  color: ${colors.petrolBlue};
                  margin-left: auto;
                  html[data-theme='dark'] & {
                    color: ${colors.skyBlue};
                  }
                `}
              >
                +{model.return}%
              </span>
            </div>
          );
        })}
      </div>

      {/* Performance chart */}
      <div
        css={css`
          flex: 1;
          background: ${colors.sugar};
          border-radius: 10px;
          padding: ${spacing.centi};
          opacity: ${showChart ? 1 : 0};
          transform: translateY(${showChart ? 0 : 12}px);
          transition:
            opacity 0.4s ease-out,
            transform 0.4s ease-out;
          display: flex;
          flex-direction: column;
          gap: 6px;
          html[data-theme='dark'] & {
            background: ${colors.liquorice};
          }
        `}
      >
        {/* Relative wrapper ensures SVG fills full width and height */}
        <div
          css={css`
            flex: 1;
            position: relative;
            min-height: 60px;
          `}
        >
          {/* Canvas chart */}
          <canvas
            ref={canvasRef}
            css={css`
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              display: block;
            `}
          />

          {/* End-of-line badges — HTML divs positioned over canvas */}
          {(Object.keys(BADGE_TOP_PCT) as Array<keyof typeof BADGE_TOP_PCT>).map((modelId) => {
            const topPct = BADGE_TOP_PCT[modelId];
            const isOpenAI = modelId === 'openai';
            const bgColor = isOpenAI ? '#111111' : CHART_COLORS[modelId];
            const bgColorDark = isOpenAI ? '#FFFFFF' : CHART_COLORS_DARK[modelId];
            return (
              <div
                key={`badge-${modelId}`}
                css={css`
                  position: absolute;
                  right: 0;
                  top: ${topPct}%;
                  transform: translateY(-50%);
                  z-index: 1;
                  background: ${bgColor};
                  border-radius: 999px;
                  padding: 3px 10px 3px 3px;
                  opacity: ${chartProgress};
                  display: flex;
                  align-items: center;
                  gap: 6px;
                  html[data-theme='dark'] & {
                    background: ${bgColorDark};
                  }
                `}
              >
                <div
                  css={css`
                    width: 22px;
                    height: 22px;
                    border-radius: 50%;
                    background: rgba(255, 255, 255, 0.25);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    ${isOpenAI ? `html[data-theme='dark'] & { background: rgba(0,0,0,0.08); }` : ''}
                  `}
                >
                  <img
                    src={MODEL_ICONS[modelId]}
                    alt={modelId}
                    css={css`
                      width: 13px;
                      height: 13px;
                      filter: brightness(0) invert(1);
                      ${isOpenAI ? `html[data-theme='dark'] & { filter: none; }` : ''}
                    `}
                  />
                </div>
                <span
                  css={css`
                    font-size: 11px;
                    font-weight: 700;
                    color: white;
                    line-height: 1;
                    font-family: sans-serif;
                    white-space: nowrap;
                    ${isOpenAI ? `html[data-theme='dark'] & { color: #111111; }` : ''}
                  `}
                >
                  {BADGE_LABELS[modelId]}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
